import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import EventCard from "@/components/EventCard";
import { Wifi, Coffee, Sun, Volume2, Printer, Users } from "lucide-react";

const HERO = "https://images.pexels.com/photos/7065/space-desk-office-hero-7065.jpg";

const AMENITIES = [
  { icon: Wifi, label: "Fibre Wi-Fi" },
  { icon: Coffee, label: "Filter coffee & tea" },
  { icon: Sun, label: "Garden views" },
  { icon: Volume2, label: "Quiet zone" },
  { icon: Printer, label: "Printing" },
  { icon: Users, label: "Meeting room" },
];

const PLANS = [
  { name: "Day Pass",   price: "₹ 700",   note: "9 AM – 6 PM, hot desk" },
  { name: "Week Pass",  price: "₹ 3,500", note: "5 working days, hot desk" },
  { name: "Monthly Hot Desk", price: "₹ 9,500", note: "Mon–Sat, any open desk" },
  { name: "Monthly Dedicated", price: "₹ 14,500", note: "Your own desk + locker" },
];

export default function Coworking() {
  const [events, setEvents] = useState([]);
  useEffect(() => {
    api.get("/events", { params: { section: "coworking" } }).then(r => setEvents(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="coworking-page">
      <section className="grid lg:grid-cols-12 gap-0 min-h-[80vh]">
        <div className="lg:col-span-6 order-1 h-[60vh] lg:h-auto">
          <img src={HERO} alt="Co-working" className="w-full h-full object-cover"/>
        </div>
        <div className="lg:col-span-6 order-2 px-6 md:px-12 lg:px-20 py-16 flex flex-col justify-center bg-cream-2">
          <div className="overline">06 — Co-working <span className="text-terra">· new</span></div>
          <h1 className="display text-6xl md:text-8xl mt-6">Work, but <em className="italic text-terra">elsewhere.</em></h1>
          <p className="mt-8 text-base text-muted2 max-w-md leading-relaxed">
            A bright, quiet floor with fast Wi-Fi, ergonomic desks and an open garden view — embedded
            inside a gallery, a cafe and a ceramic studio. Day passes from ₹ 700.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to="/events?section=coworking" className="btn-primary" data-testid="cowork-book-day">Book a day pass</Link>
            <Link to="/contact" className="btn-ghost" data-testid="cowork-tour">Tour the space</Link>
          </div>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24 py-24">
        <div className="overline">Amenities</div>
        <h2 className="display text-4xl md:text-5xl mt-4">What&apos;s included.</h2>
        <div className="mt-12 grid grid-cols-2 md:grid-cols-3 gap-6">
          {AMENITIES.map(({ icon: Icon, label }) => (
            <div key={label} className="border border-soft p-6 flex items-center gap-4">
              <Icon size={20} className="text-sage" />
              <span className="text-sm">{label}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-cream-2 py-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="overline">Plans</div>
          <h2 className="display text-4xl md:text-5xl mt-4">Pricing.</h2>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {PLANS.map((p, i) => (
              <div key={p.name} className={`border border-soft p-8 bg-cream ${i === 1 ? "lg:translate-y-6" : ""}`}>
                <div className="overline">0{i + 1}</div>
                <div className="font-serif text-3xl mt-4 tracking-tight">{p.name}</div>
                <div className="font-serif text-5xl mt-6">{p.price}</div>
                <div className="text-sm text-muted2 mt-3">{p.note}</div>
                <Link to="/events?section=coworking" className="btn-ghost mt-6 w-full justify-center" data-testid={`cowork-plan-${i}`}>Book</Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {events.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24 py-24">
          <div className="overline">Available</div>
          <h2 className="display text-4xl md:text-5xl mt-4">Book a desk.</h2>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-10">
            {events.map(e => <EventCard key={e.id} event={e} />)}
          </div>
        </section>
      )}
    </div>
  );
}
